import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  Building2,
  Users,
  UserCheck,
  ChevronRight,
  TrendingUp,
  Activity,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// ── SHARED COMPONENTS ───────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color, trend }) => (
  <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1 cursor-default">
    <div className={`h-1 w-8 rounded-full mb-4 ${color}`} />
    <div className="flex justify-between items-start">
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{label}</p>
        <h3 className="text-2xl font-poppins font-bold mt-1 text-gray-800">{value}</h3>
      </div>
      <div className="p-2 rounded-lg bg-gray-50 text-gray-600">
        <Icon size={18} />
      </div>
    </div>
    <div className="mt-4 flex items-center gap-2">
      <span className="text-emerald-500 text-xs font-bold flex items-center bg-emerald-50 px-1.5 py-0.5 rounded">
        <TrendingUp size={12} className="mr-1" /> {trend}
      </span>
      <span className="text-gray-400 text-[11px]">vs last month</span>
    </div>
  </div>
);

// ── PENDING APPROVAL ITEM (FUNCTIONAL) ───────────────────────────────────
const PendingApprovalItem = ({ id, companyName, adminName, timeAgo }) => {
  const [status, setStatus] = React.useState('pending');

  const handleApprove = () => setStatus('approved');
  const handleReject = () => setStatus('rejected');

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg group hover:bg-indigo-50 transition-all duration-200">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-md">
          {companyName.charAt(0)}
        </div>
        <div>
          <p className="font-semibold text-gray-800">New Registration #{id}</p>
          <p className="text-xs text-gray-500">{companyName} • {adminName}</p>
          <p className="text-xs text-gray-400">{timeAgo}</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {status === 'pending' ? (
          <>
            <button
              onClick={handleApprove}
              className="p-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors"
              title="Approve"
            >
              <CheckCircle size={16} />
            </button>
            <button
              onClick={handleReject}
              className="p-2 bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200 transition-colors"
              title="Reject"
            >
              <XCircle size={16} />
            </button>
          </>
        ) : (
          <span
            className={`text-xs font-bold px-3 py-1 rounded-full ${
              status === 'approved'
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-rose-100 text-rose-700'
            }`}
          >
            {status === 'approved' ? 'Approved' : 'Rejected'}
          </span>
        )}
        <ChevronRight size={16} className="text-gray-400 group-hover:text-indigo-600 transition-colors" />
      </div>
    </div>
  );
};

// ── MAIN DASHBOARD ──────────────────────────────────────────────────────
const Dashboard = () => {
  const stats = [
    { label: "Total Companies", value: "1,284", icon: Building2, color: "bg-blue-500", trend: "+12%" },
    { label: "Active Companies", value: "1,192", icon: UserCheck, color: "bg-emerald-500", trend: "+5.4%" },
    { label: "Super Admins", value: "2,408", icon: Users, color: "bg-indigo-500", trend: "+8%" },
    { label: "Total Employees", value: "48,902", icon: Activity, color: "bg-amber-500", trend: "+18%" },
  ];

  const monthlyGrowthData = [
    { month: 'Jan', companies: 65 },
    { month: 'Feb', companies: 82 },
    { month: 'Mar', companies: 91 },
    { month: 'Apr', companies: 108 },
    { month: 'May', companies: 133 },
    { month: 'Jun', companies: 162 },
    { month: 'Jul', companies: 189 },
    { month: 'Aug', companies: 215 },
    { month: 'Sep', companies: 248 },
    { month: 'Oct', companies: 292 },
    { month: 'Nov', companies: 340 },
    { month: 'Dec', companies: 398 },
  ];

  const pendingApprovals = [
    { id: '10', companyName: 'Nexlify Solutions', adminName: 'Rahul Kumar', timeAgo: '12 mins ago' },
    { id: '09', companyName: 'QuantumSoft Pvt Ltd', adminName: 'Priya Singh', timeAgo: '24 mins ago' },
    { id: '08', companyName: 'Alpha Dynamics', adminName: 'Aman Verma', timeAgo: '1 hour ago' },
  ];

  const tabs = [
    { label: 'Overview', path: '/global-admin', icon: LayoutGrid },
    { label: 'Companies', path: '/global-admin/companies', icon: Building2 },
    { label: 'Super Admins', path: '/global-admin/super-admins', icon: Users },
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 px-8 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center gap-10">
          {tabs.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              end
              className={({ isActive }) =>
                `flex items-center gap-2 py-5 border-b-2 text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  isActive
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`
              }
            >
              <tab.icon size={18} />
              {tab.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8 max-w-7xl mx-auto">
        <div className="flex justify-between items-end mb-8">
          <div>
            <h1 className="font-poppins font-bold text-3xl text-gray-900">System Overview</h1>
            <p className="text-gray-500 text-sm mt-1">Real-time platform health & infrastructure monitoring</p>
          </div>
          <button className="bg-indigo-600 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-all shadow-lg flex items-center gap-2 hover:shadow-xl">
            <Activity size={18} />
            Generate Monthly Report
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {stats.map((s, idx) => (
            <StatCard key={idx} {...s} />
          ))}
        </div>

        {/* Charts + Pending Approvals */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Charts Area */}
          <div className="lg:col-span-2 space-y-8">
            {/* Growth Line Chart */}
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="font-poppins font-semibold text-gray-800 text-lg">Company Growth (2025)</h3>
                  <p className="text-sm text-gray-500">New companies onboarded monthly</p>
                </div>
                <span className="text-2xl font-bold text-emerald-500">+42.8%</span>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={monthlyGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#e5e7eb' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="companies"
                    stroke="#4f46e5"
                    strokeWidth={3}
                    dot={{ fill: '#4f46e5', r: 6 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Activity Bar Chart */}
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
              <h3 className="font-poppins font-semibold text-gray-800 mb-4">System Activity Last 7 Days</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={[
                  { day: 'Mon', logins: 2400 },
                  { day: 'Tue', logins: 3210 },
                  { day: 'Wed', logins: 2890 },
                  { day: 'Thu', logins: 3520 },
                  { day: 'Fri', logins: 4100 },
                  { day: 'Sat', logins: 2800 },
                  { day: 'Sun', logins: 1900 },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="logins" fill="#10b981" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Pending Approvals Panel */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm h-fit">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-poppins font-semibold text-gray-800">Pending Approvals</h3>
              <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full">
                {pendingApprovals.length} new
              </span>
            </div>
            <div className="space-y-4">
              {pendingApprovals.map((item) => (
                <PendingApprovalItem key={item.id} {...item} />
              ))}
            </div>
            <button className="w-full mt-6 py-3 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-indigo-200">
              View All Requests →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;